import "./App.css";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useEffect } from "react";
// Main router lives here so global tracking and layout stay consistent.
import { BrowserRouter as Router } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import Seo from "./components/Seo";
import ArticleLayout from "./components/ArticleLayout";
import ScrollToTop from "./components/ScrollToTop";
import Register from "./pages/automation_practice/Register.jsx";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import LoginPage from "./pages/LoginPage";
import AdminDashboard from "./pages/admin/AdminDashboard";
import TrainerDashboard from "./pages/trainer/TrainerDashboard";
import StudentDashboard from "./pages/StudentDashboard";
import LeadCRM from "./pages/LeadCRM";
import BugTracker from "./pages/BugTracker";
import RaiseBug from "./pages/RaiseBug";
import BugDetail from "./pages/BugDetail";
import UnauthorizedPage from "./pages/UnauthorizedPage";
import ProtectedRoute from "./components/ProtectedRoute";
import SignupPage from "./pages/SignupPage.jsx";
import OnboardingPage from "./pages/OnboardingPage.jsx";
import ManualTestingClusterPage from "./pages/ManualTestingClusterPage.jsx";
import AutomationTestingClusterPage from "./pages/AutomationTestingClusterPage.jsx";
import ApiTestingClusterPage from "./pages/ApiTestingClusterPage.jsx";
import InterviewQuestionsClusterPage from "./pages/InterviewQuestionsClusterPage.jsx";
import PracticalExampleClusterPage from "./pages/PracticalExampleClusterPage.jsx";
import BlogSeoPage from "./pages/BlogSeoPage.jsx";
import SeoContentPage from "./pages/SeoContentPage.jsx";
import { trackPageView } from "./services/analyticsService";
import { getSeoConfig } from "./seo/seoConfig";
import { getArticleLayoutConfig } from "./content/articleLayoutConfig";
import { manualTestingClusterPages } from "./content/manualTestingCluster";
import { automationTestingClusterPages } from "./content/automationTestingCluster";
import { apiTestingClusterPages } from "./content/apiTestingCluster";
import { interviewQuestionsClusterPages } from "./content/interviewQuestionsCluster";
import { practicalExampleClusterPages } from "./content/practicalExampleCluster";
import { blogSeoPages } from "./content/blogSeoCluster";
import { seoContentPages } from "./content/seoContentPages";
import { routePaths } from "./constants/routes";

import {
  Home, AboutUs, Contact, Product, Automation, FlaseSales,
  SoftwareTesting, TestingTypes, TestingPyramid, TestingMethods,
  TestingPractices, TestingTools, UnitTesting, MobilePhone,
  ProductDetails, AddToCart, AddressPage, PaymentPage, OrderHistory, OrderSuccess, ProductSearchListing, InvoicePage, OrderTrackingPage,
  AutomationTesting, BestPracticeToCreateIssue, SmokeVsRegression,
  IntegrationTesting, BlackBoxTesting, RegressionTesting,
  SuccessPage, ManualTesting, ManualEssentialSkill, ManualVsAutomationTesting, SdlcPage,
  WhiteBoxBlackBoxPage, TopRegressionTestingTools, TestCasesVsTestSuites,
  TestPlanTemplate, TestEnvironmentPage, FunctionalTestingGuide,
  BlogArchive, SmokeTesting, PerformanceTesting, TestCoverage,
  TopTools, AutomationTestingTopic, TestAutomationROI, SanityTesting,
  AcceptanceTesting, APITesting, ExploratoryTesting, GrayBox,
  WhiteBox, BlackWhiteGray, APITestingCheatSheet, EmployeeList,
  AddEmployee, LeaveList, StatusCode, EnrollPage, StudyNotes, PremiumNotesStore, JiraNotes, JiraNotesCheckout, JiraNotesDownload,
  Blog, APITestingInterview,
  PrivacyPolicy, TermsPage, RefundPolicy, NotFound,
} from "./pages";

// Component handles content routing and protection logic.
function AppContent() {
  const location = useLocation();
  const seoConfig = getSeoConfig(location.pathname);

  const renderArticlePage = (pathname, pageElement) => (
    <ArticleLayout config={getArticleLayoutConfig(pathname)} currentPath={pathname}>
      {pageElement}
    </ArticleLayout>
  );

  useEffect(() => {
    if (!sessionStorage.getItem("qaCoderFirstLandingPage")) {
      sessionStorage.setItem("qaCoderFirstLandingPage", window.location.href);
    }

    const previousPath = sessionStorage.getItem("qaCoderPreviousPath");

    trackPageView({
      path: location.pathname,
      title: document.title,
      previousPath,
    });

    sessionStorage.setItem("qaCoderPreviousPath", location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    const allowedPages = [
      routePaths.login,
      routePaths.signup,
      "/register",
      routePaths.bugs,
      routePaths.bugsCreate,
      routePaths.myBugs,
      routePaths.assignedBugs,
    ];
    const allowedPrefixes = [
      "/automation-",
      "/test-automation-",
    ];
    const isAllowedPage =
      allowedPages.includes(location.pathname) ||
      location.pathname.startsWith(`${routePaths.bugs}/`) ||
      allowedPrefixes.some((prefix) => location.pathname.startsWith(prefix));

    const preventDefault = (e) => {
      if (!isAllowedPage) e.preventDefault();
    };

    const handleKeyDown = (e) => {
      if (!isAllowedPage && (
        (e.ctrlKey && ["c", "x", "v", "u", "s"].includes(e.key.toLowerCase())) ||
        e.key === "F12"
      )) {
        e.preventDefault();
      }
    };

    document.addEventListener("contextmenu", preventDefault);
    document.addEventListener("copy", preventDefault);
    document.addEventListener("cut", preventDefault);
    document.addEventListener("paste", preventDefault);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("contextmenu", preventDefault);
      document.removeEventListener("copy", preventDefault);
      document.removeEventListener("cut", preventDefault);
      document.removeEventListener("paste", preventDefault);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [location.pathname]);

  return (
    <div className="site-container">
      <Seo config={seoConfig} pathname={location.pathname} />
      <ScrollToTop />
      <Navbar title="QaCoder" aboutText="About Us" />
      
      <main className="app-content" style={{ flex: 1 }}>
        <Routes>
          <Route path="/" element={<Navigate to={routePaths.home} replace />} />
          <Route path={routePaths.enroll} element={<EnrollPage />} />
          <Route path={routePaths.home} element={<Home />} />
          <Route path={routePaths.automationTestcases} element={<Automation />} />
          <Route path="/product" element={<Product />} />
          <Route path="/signup-page" element={<SignupPage />} />
          <Route path="/register" element={<Register />} />
          <Route path="/broken-link-status-code" element={<StatusCode />} />
          <Route path={routePaths.about} element={<AboutUs />} />
          <Route path={routePaths.apiTesting} element={renderArticlePage(routePaths.apiTesting, <APITesting />)} />
          <Route path={routePaths.contact} element={<Contact />} />
          <Route path={routePaths.automationTesting} element={<AutomationTesting />} />
          {blogSeoPages.map((page) => (
            <Route
              key={page.path}
              path={page.path}
              element={<BlogSeoPage slug={page.slug} />}
            />
          ))}
          <Route path="/testing-pyramid" element={renderArticlePage("/testing-pyramid", <TestingPyramid />)} />
          <Route path={routePaths.cart} element={<AddToCart />} />
          <Route path={routePaths.address} element={<AddressPage />} />
          <Route path={routePaths.payment} element={<PaymentPage />} />
          <Route path="/acceptance-testing" element={renderArticlePage("/acceptance-testing", <AcceptanceTesting />)} />
          <Route path="/black-box-testing" element={renderArticlePage("/black-box-testing", <BlackBoxTesting />)} />
          <Route path="/functional-testing-guide" element={renderArticlePage("/functional-testing-guide", <FunctionalTestingGuide />)} />
          <Route path="/create-issue" element={renderArticlePage("/create-issue", <BestPracticeToCreateIssue />)} />
          <Route path="/what-is-exploratory-testing" element={renderArticlePage("/what-is-exploratory-testing", <ExploratoryTesting />)} />
          <Route path="/what-is-gray-box-testing" element={renderArticlePage("/what-is-gray-box-testing", <GrayBox />)} />
          <Route path="/employee-list" element={<EmployeeList />} />
          <Route path="/leave-list" element={<LeaveList />} />
          <Route path="/add-employee" element={<AddEmployee />} />
          <Route path="/flash-sales" element={<FlaseSales />} />
          <Route path={routePaths.productSearch} element={<ProductSearchListing />} />
          <Route path={routePaths.softwareTesting} element={renderArticlePage(routePaths.softwareTesting, <SoftwareTesting />)} />
          <Route path="/testing-types" element={renderArticlePage("/testing-types", <TestingTypes />)} />
          <Route path="/integration-testing" element={renderArticlePage("/integration-testing", <IntegrationTesting />)} />
          <Route path="/integretion-testing" element={<Navigate to="/integration-testing" replace />} />
          <Route path="/testing-methods" element={renderArticlePage("/testing-methods", <TestingMethods />)} />
          <Route path="/testing-practices" element={renderArticlePage("/testing-practices", <TestingPractices />)} />
          <Route path="/testing-tools" element={renderArticlePage("/testing-tools", <TestingTools />)} />
          <Route path="/product-details" element={<ProductDetails />} />
          <Route path={routePaths.automationTestingTopic} element={renderArticlePage(routePaths.automationTestingTopic, <AutomationTestingTopic />)} />
          <Route path={routePaths.interviewQuestions} element={renderArticlePage(routePaths.interviewQuestions, <InterviewQuestionsClusterPage slug="testing-interview-questions" />)} />
          <Route path="/smoke-vs-regression" element={renderArticlePage("/smoke-vs-regression", <SmokeVsRegression />)} />
          <Route path="/white-box-testing" element={renderArticlePage("/white-box-testing", <WhiteBox />)} />
          <Route path="/unit-testing" element={renderArticlePage("/unit-testing", <UnitTesting />)} />
          <Route path="/product_phone_tablets" element={<MobilePhone />} />
          <Route path={routePaths.orderHistory} element={<OrderHistory />} />
          <Route path={routePaths.orderSuccess} element={<OrderSuccess />} />
          <Route path={routePaths.invoice()} element={<InvoicePage />} />
          <Route path={routePaths.orderTracking()} element={<OrderTrackingPage />} />
          <Route path="/product-details/:id" element={<ProductDetails />} />
          <Route path="/regression-testing" element={renderArticlePage("/regression-testing", <RegressionTesting />)} />
          <Route path="/success" element={<SuccessPage />} />
          <Route path="/API-testing-cheatSheet" element={renderArticlePage("/API-testing-cheatSheet", <APITestingCheatSheet />)} />
          <Route path="/test-automation-tools" element={renderArticlePage("/test-automation-tools", <TopTools />)} />
          <Route path="/testing-types-blog" element={<BlogArchive />} />
          <Route path="/testing-blog" element={renderArticlePage("/testing-blog", <Blog />)} />
          <Route path="/what-is-sdlc" element={renderArticlePage("/what-is-sdlc", <SdlcPage />)} />
          <Route path="/what-is-sanity" element={renderArticlePage("/what-is-sanity", <SanityTesting />)} />
          <Route path="/what-is-performance-testing" element={renderArticlePage("/what-is-performance-testing", <PerformanceTesting />)} />
          <Route path="/what-is-test-caverage" element={renderArticlePage("/what-is-test-caverage", <TestCoverage />)} />
          <Route path="/test-environment-page" element={renderArticlePage("/test-environment-page", <TestEnvironmentPage />)} />
          <Route path="/test-automation-roi" element={renderArticlePage("/test-automation-roi", <TestAutomationROI />)} />
          <Route path="/test-cases-vs-test-suites-understanding-vs-difference" element={renderArticlePage("/test-cases-vs-test-suites-understanding-vs-difference", <TestCasesVsTestSuites />)} />
          <Route path="/top-regression-testing-tools" element={renderArticlePage("/top-regression-testing-tools", <TopRegressionTestingTools />)} />
          <Route path="/Top-5-Regression-Testing-Tools–Overview" element={<Navigate to="/top-regression-testing-tools" replace />} />
          <Route path="/Top-5-Regression-Testing-Toolsâ€“Overview" element={<Navigate to="/top-regression-testing-tools" replace />} />
          <Route path={routePaths.manualTesting} element={renderArticlePage(routePaths.manualTesting, <ManualTesting />)} />
          <Route path="/manual-testing-essential-skills" element={renderArticlePage("/manual-testing-essential-skills", <ManualEssentialSkill />)} />
          <Route path="/black-vs-white-vs-gray-box-testing" element={renderArticlePage("/black-vs-white-vs-gray-box-testing", <BlackWhiteGray />)} />
          <Route path="/what-is-smoke-testing" element={renderArticlePage("/what-is-smoke-testing", <SmokeTesting />)} />
          <Route path="/test-plan-template" element={renderArticlePage("/test-plan-template", <TestPlanTemplate />)} />
          <Route path="/what-is-white-box-and-black-box-testing" element={renderArticlePage("/what-is-white-box-and-black-box-testing", <WhiteBoxBlackBoxPage />)} />
          <Route path="/manual-vs-automation-testing" element={renderArticlePage("/manual-vs-automation-testing", <ManualVsAutomationTesting />)} />
          <Route path={routePaths.studyNotes} element={renderArticlePage(routePaths.studyNotes, <StudyNotes />)} />
          <Route path={routePaths.premiumNotes} element={<PremiumNotesStore />} />
          <Route path={routePaths.jiraNotes} element={renderArticlePage(routePaths.jiraNotes, <JiraNotes />)} />
          <Route path={routePaths.jiraNotesCheckout} element={<JiraNotesCheckout />} />
          <Route path={routePaths.jiraNotesDownload} element={<JiraNotesDownload />} />
          <Route path="/api-testing-interview" element={renderArticlePage("/api-testing-interview", <APITestingInterview />)} />
          {interviewQuestionsClusterPages
            .filter((page) => page.path !== routePaths.interviewQuestions)
            .map((page) => (
              <Route
                key={page.path}
                path={page.path}
                element={renderArticlePage(page.path, <InterviewQuestionsClusterPage slug={page.slug} />)}
              />
            ))}
          {manualTestingClusterPages.map((page) => (
            <Route
              key={page.path}
              path={page.path}
              element={renderArticlePage(page.path, <ManualTestingClusterPage slug={page.slug} />)}
            />
          ))}
          {automationTestingClusterPages.map((page) => (
            <Route
              key={page.path}
              path={page.path}
              element={renderArticlePage(page.path, <AutomationTestingClusterPage slug={page.slug} />)}
            />
          ))}
          {apiTestingClusterPages.map((page) => (
            <Route
              key={page.path}
              path={page.path}
              element={renderArticlePage(page.path, <ApiTestingClusterPage slug={page.slug} />)}
            />
          ))}
          {practicalExampleClusterPages.map((page) => (
            <Route
              key={page.path}
              path={page.path}
              element={renderArticlePage(page.path, <PracticalExampleClusterPage slug={page.slug} />)}
            />
          ))}
          {seoContentPages.map((page) => (
            <Route
              key={page.path}
              path={page.path}
              element={<SeoContentPage slug={page.slug} />}
            />
          ))}
          <Route path={routePaths.login} element={<LoginPage />} />
          <Route path={routePaths.unauthorized} element={<UnauthorizedPage />} />
          <Route path={routePaths.privacyPolicy} element={<PrivacyPolicy />} />
          <Route path={routePaths.terms} element={<TermsPage />} />
          <Route path={routePaths.refundPolicy} element={<RefundPolicy />} />
          <Route path={routePaths.onboarding} element={
            <ProtectedRoute>
              <OnboardingPage />
            </ProtectedRoute>
          } />

          {/* Protected Routes */}
          <Route path={routePaths.admin} element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          <Route path={routePaths.trainer} element={
            <ProtectedRoute allowedRoles={["trainer"]}>
              <TrainerDashboard />
            </ProtectedRoute>
          } />
          <Route path={routePaths.leadCrm} element={
            <ProtectedRoute allowedRoles={["admin", "trainer"]}>
              <LeadCRM />
            </ProtectedRoute>
          } />
          <Route path={routePaths.bugs} element={
            <ProtectedRoute allowedRoles={["admin", "trainer", "student"]}>
              <BugTracker />
            </ProtectedRoute>
          } />
          <Route path={routePaths.myBugs} element={
            <ProtectedRoute allowedRoles={["admin", "trainer", "student"]}>
              <BugTracker />
            </ProtectedRoute>
          } />
          <Route path={routePaths.assignedBugs} element={
            <ProtectedRoute allowedRoles={["admin", "trainer"]}>
              <BugTracker />
            </ProtectedRoute>
          } />
          <Route path={routePaths.bugsCreate} element={
            <ProtectedRoute allowedRoles={["admin", "trainer", "student"]}>
              <RaiseBug />
            </ProtectedRoute>
          } />
          <Route path={routePaths.bugDetail()} element={
            <ProtectedRoute allowedRoles={["admin", "trainer", "student"]}>
              <BugDetail />
            </ProtectedRoute>
          } />
          <Route path={routePaths.student} element={
            <ProtectedRoute allowedRoles={["student"]}>
              <StudentDashboard />
            </ProtectedRoute>
          } />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>

      <Footer />
      <ToastContainer position="top-right" autoClose={2000} />
    </div>
  );
}



function App() {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
}

export default App;
